import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {
  TimelineModifyingState,
  TimelineModifyingAction,
  TimelineBarRef,
  TimelinePointerDropTarget,
  TimelineGroupGestureMember,
  TimelineSelectionExtensionUids,
} from './timeline.types';

export const TIMELINE_MIN_WIDTH_PER_SECOND = 5;
export const TIMELINE_MAX_WIDTH_PER_SECOND = 500;
/** Pre-open placeholder zoom, replaced by the duration-fitted value on the timeline's first open (initializeTimelineZoom). */
export const DEFAULT_TIMELINE_WIDTH_PER_SECOND = 47;

interface SubtitlePushedPosition {
  startTime: number;
  endTime: number;
}

interface TimelineTooltipState {
  left: number;
  top: number;
  text: string;
  /** Center the tooltip on `left` instead of the legacy fixed offset — used by boundary-anchored tooltips. */
  isCentered?: boolean;
}

/** The timeline resize handle the pointer is over — lets boundary chips yield to an adjacent handle. */
export interface HoveredResizeHandleState {
  itemId: string;
  side: 'left' | 'right';
}

interface TimelineState {
  widthPerSecond: number;
  timelineHeight: number;
  isPanning: boolean;
  /** ID of the currently hovered timeline item */
  hoveredItemId?: string;
  /** Current item being modified (dragged or resized). Undefined when no modification in progress. */
  modifying?: TimelineModifyingState;
  timelineHoverPosition?: number;
  /** Temporary positions for subtitles being pushed during a resize operation */
  pushedSubtitlePositions: Record<string, SubtitlePushedPosition>;
  /** Whether the seekbar is currently being dragged */
  isSeekbarDragging: boolean;
  /** Pixel positions of active snap guide lines (shown during drag/resize) */
  snapGuideLines: number[];
  /** Whether the timeline panel is currently focused (last clicked area was the timeline) */
  isFocused: boolean;
  /** Tooltip shown during resize operations */
  tooltip?: TimelineTooltipState;
  /** The resize handle currently hovered, if any. */
  hoveredResizeHandle?: HoveredResizeHandleState;
  /**
   * True while the pointer is over an element that suppresses the hover seek-line (resize
   * handles, boundary chips). Only the LINE hides — timelineHoverPosition keeps updating,
   * because the boundary chips' hover-reveal reads it and must not lose it mid-hover.
   */
  isHoverLineSuppressed: boolean;
  /**
   * The bar a shift-click extends FROM: the last bar the user plainly clicked or
   * cmd/ctrl-toggled. Successive shift-clicks re-extend from the same anchor. May go
   * stale (bar deleted, page switched) — readers must resolve it against live bars.
   */
  selectionAnchor?: TimelineBarRef;
  /**
   * The bars the LAST shift-click extended the selection over. The next shift-click
   * from the same anchor replaces only these bars and preserves selections made outside
   * them (cmd-clicked strays survive, extensions can shrink). Cleared whenever the
   * anchor moves — a new anchor starts a fresh extension over the then-current selection.
   */
  lastSelectionExtension?: TimelineSelectionExtensionUids;
  /** Whether the first-open duration-fitted zoom has been applied this session (see initializeTimelineZoom). */
  isZoomInitialized: boolean;
}

const initialState: TimelineState = {
  widthPerSecond: DEFAULT_TIMELINE_WIDTH_PER_SECOND,
  isZoomInitialized: false,
  timelineHeight: 0,
  isPanning: false,
  hoveredItemId: undefined,
  modifying: undefined,
  timelineHoverPosition: undefined,
  isHoverLineSuppressed: false,
  pushedSubtitlePositions: {},
  isSeekbarDragging: false,
  snapGuideLines: [],
  isFocused: false,
  tooltip: undefined,
  selectionAnchor: undefined,
  lastSelectionExtension: undefined,
};

/** Payload for starting a timeline gesture (drag or resize) */
interface StartModifyingPayload {
  grabbed: TimelineBarRef;
  action: TimelineModifyingAction;
  startTime: number;
  endTime: number;
  originalSubRow?: number;
  groupMembers?: TimelineGroupGestureMember[];
}

/**
 * Payload for updating the in-flight gesture on every pointermove. Snap lines and the
 * tooltip ride along so one dispatch per move carries all gesture feedback — three
 * separate dispatches were measurable overhead at pointermove rates.
 */
interface UpdateModifyingPayload {
  startTime: number;
  endTime: number;
  targetSubRow?: number;
  insertionIndex?: number;
  dragDeltaY?: number;
  dropTarget?: TimelinePointerDropTarget;
  snapGuideLines?: number[];
  tooltip?: TimelineTooltipState;
}

// Dispatched on every pointermove; keep the old reference when content is unchanged so subscribers don't re-render per move.
const applySnapGuideLines = (state: TimelineState, snapGuideLines: number[]): void => {
  const isUnchanged = state.snapGuideLines.length === snapGuideLines.length && snapGuideLines.every((line, index) => line === state.snapGuideLines[index]);
  if (!isUnchanged) {
    state.snapGuideLines = snapGuideLines;
  }
};

const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    updateTimelineWidthPerSecond: (state, action: PayloadAction<number>): void => {
      if (action.payload < TIMELINE_MIN_WIDTH_PER_SECOND) {
        state.widthPerSecond = TIMELINE_MIN_WIDTH_PER_SECOND;
      } else if (action.payload > TIMELINE_MAX_WIDTH_PER_SECOND) {
        state.widthPerSecond = TIMELINE_MAX_WIDTH_PER_SECOND;
      } else {
        state.widthPerSecond = action.payload;
      }
    },
    updatePanningStatus: (state, action: PayloadAction<boolean>): void => {
      state.isPanning = action.payload;
    },
    setHoveredItemId: (state, action: PayloadAction<string | undefined>): void => {
      state.hoveredItemId = action.payload;
    },
    setTimelineSelectionAnchor: (state, action: PayloadAction<TimelineBarRef | undefined>): void => {
      state.selectionAnchor = action.payload;
      state.lastSelectionExtension = undefined;
    },
    setTimelineLastSelectionExtension: (state, action: PayloadAction<TimelineSelectionExtensionUids | undefined>): void => {
      state.lastSelectionExtension = action.payload;
    },
    /**
     * Start a timeline gesture (drag or resize).
     * Sets up the modifying state with initial values.
     */
    startModifying: (state, action: PayloadAction<StartModifyingPayload>): void => {
      state.modifying = {
        grabbed: action.payload.grabbed,
        action: action.payload.action,
        startTime: action.payload.startTime,
        endTime: action.payload.endTime,
        originalSubRow: action.payload.originalSubRow,
        groupMembers: action.payload.groupMembers,
      };
    },
    /**
     * Update the current modification in progress.
     * Called during pointermove to update visual position.
     */
    updateModifying: (state, action: PayloadAction<UpdateModifyingPayload>): void => {
      if (state.modifying) {
        state.modifying.startTime = action.payload.startTime;
        state.modifying.endTime = action.payload.endTime;
        state.modifying.targetSubRow = action.payload.targetSubRow;
        state.modifying.insertionIndex = action.payload.insertionIndex;
        state.modifying.dragDeltaY = action.payload.dragDeltaY;
        state.modifying.dropTarget = action.payload.dropTarget;
        if (action.payload.snapGuideLines !== undefined) {
          applySnapGuideLines(state, action.payload.snapGuideLines);
        }
        if (action.payload.tooltip !== undefined) {
          state.tooltip = action.payload.tooltip;
        }
      }
    },
    /**
     * End the current modification.
     * Clears the modifying state and any pushed subtitle positions.
     */
    endModifying: (state): void => {
      state.modifying = undefined;
      state.pushedSubtitlePositions = {};
      state.snapGuideLines = [];
      state.tooltip = undefined;
    },
    /**
     * Set temporary pushed positions for subtitles being displaced during a resize.
     * Replaces any previously set pushed positions.
     */
    setPushedSubtitlePositions: (state, action: PayloadAction<Record<string, SubtitlePushedPosition>>): void => {
      state.pushedSubtitlePositions = action.payload;
    },
    updateTimelineHoverPosition: (state, action: PayloadAction<number | undefined>): void => {
      state.timelineHoverPosition = action.payload;
    },
    /**
     * Sets the first-open zoom, fitted to the page duration by useInitialTimelineZoom.
     * Applies once per session — after that the user's zoom choice sticks across timeline
     * closes/reopens and page switches.
     */
    initializeTimelineZoom: (state, action: PayloadAction<number>): void => {
      if (state.isZoomInitialized) {
        return;
      }
      state.isZoomInitialized = true;
      state.widthPerSecond = Math.min(TIMELINE_MAX_WIDTH_PER_SECOND, Math.max(TIMELINE_MIN_WIDTH_PER_SECOND, action.payload));
    },
    updateTimelineHeight: (state, action: PayloadAction<number>): void => {
      state.timelineHeight = action.payload;
    },
    setSeekbarDragging: (state, action: PayloadAction<boolean>): void => {
      state.isSeekbarDragging = action.payload;
    },
    setTimelineFocused: (state, action: PayloadAction<boolean>): void => {
      state.isFocused = action.payload;
    },
    setTimelineTooltip: (state, action: PayloadAction<TimelineTooltipState | undefined>): void => {
      state.tooltip = action.payload;
    },
    setHoveredResizeHandle: (state, action: PayloadAction<HoveredResizeHandleState | undefined>): void => {
      state.hoveredResizeHandle = action.payload;
    },
    setIsHoverLineSuppressed: (state, action: PayloadAction<boolean>): void => {
      state.isHoverLineSuppressed = action.payload;
    },
  },
});

export const {
  updateTimelineWidthPerSecond,
  updateTimelineHeight,
  updatePanningStatus,
  setHoveredItemId,
  startModifying,
  updateModifying,
  endModifying,
  updateTimelineHoverPosition,
  setPushedSubtitlePositions,
  setSeekbarDragging,
  setTimelineFocused,
  setTimelineTooltip,
  setHoveredResizeHandle,
  setIsHoverLineSuppressed,
  setTimelineSelectionAnchor,
  setTimelineLastSelectionExtension,
  initializeTimelineZoom,
} = timelineSlice.actions;

export const timelineReducer = timelineSlice.reducer;
